/**
 * Theme Store
 *
 * Manages theme state including theme selection and dark mode.
 * Both themeName and isDarkMode are persisted to localStorage so the correct
 * theme is applied instantly on page refresh (no flash of purple).
 * On logout the theme is reset to 'purple' so it doesn't leak to the next user.
 *
 * @module store/theme/themeStore
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { persist } from 'zustand/middleware';
import { createPersistConfig } from '../middleware/persistenceMiddleware';
import {
  themes,
  lightModeColors,
  darkModeColors,
  getThemeNameFromHex,
  getHexFromThemeName,
  extractThemeFromUserData,
  generatePaletteFromHex
} from './themeConstants';
import { applyThemeToDOM, dispatchThemeChangedEvent } from './themeEffects';
import { asyncStorage } from '../../shared/services/storage/asyncStorageService';
import logger from '../../shared/services/logging/logger';

/**
 * Default theme — used on first visit or after logout clears the persisted value.
 */
const DEFAULT_THEME = 'purple';

/**
 * Read persisted theme synchronously so the correct colour is applied
 * on the very first render — no flash of purple on refresh.
 * @returns {string} Persisted theme name or DEFAULT_THEME
 */
const getInitialTheme = () => {
  try {
    // ecoh-custom-theme is written outside Zustand persist and survives logout resets.
    // Check it first so a returning user's custom colour is applied immediately,
    // with no purple flash while OrganizationPreloader re-hydrates it.
    const customHex = localStorage.getItem('ecoh-custom-theme');
    if (customHex) return `custom:${customHex}`;

    const stored = asyncStorage.getJSON('theme-storage');
    const name = stored?.state?.themeName;
    if (name && (themes[name] || name.startsWith('custom:'))) return name;
  } catch { /* ignore */ }
  return DEFAULT_THEME;
};

/**
 * Get initial dark mode from storage
 * @returns {boolean} Dark mode enabled
 */
const getInitialDarkMode = () => {
  return asyncStorage.get('darkMode') === 'true';
};

/**
 * Theme store state and actions
 *
 * @typedef {Object} ThemeState
 * @property {string} themeName - Current theme name
 * @property {boolean} isDarkMode - Dark mode enabled
 * @property {Function} setTheme - Set theme by name or hex
 * @property {Function} toggleDarkMode - Toggle dark mode
 * @property {Function} setDarkMode - Set dark mode explicitly
 * @property {Function} hydrateFromUserData - Hydrate theme from user data
 */

export const useThemeStore = create(
  persist(
    (set, get) => ({
      // State
      themeName: getInitialTheme(),
      isDarkMode: getInitialDarkMode(),

      // Actions

      /**
       * Set theme by name or hex code
       * @param {string} themeValue - Theme name or hex code
       */
      setTheme: (themeValue) => {
        let themeName;

        if (themeValue.startsWith('custom:')) {
          // Custom hex theme — store as-is
          themeName = themeValue;
        } else if (themeValue.startsWith('#')) {
          themeName = getThemeNameFromHex(themeValue);
        } else {
          themeName = themeValue;
        }

        if (!themes[themeName] && !themeName.startsWith('custom:')) {
          logger.warn('Invalid theme name', { themeName });
          return;
        }

        // Keep a separate backup for custom themes so it survives logout.
        // Only setTheme saves it here — clearing happens explicitly in the UI
        // when the user picks a standard theme, NOT during logout resets.
        try {
          if (themeName.startsWith('custom:')) {
            localStorage.setItem('ecoh-custom-theme', themeName.slice(7));
          }
        } catch { /* ignore */ }

        logger.info('Setting theme', { themeName });

        set({ themeName });

        // Apply DOM changes
        applyThemeToDOM(themeName, get().isDarkMode);

        // Dispatch custom event
        const primaryHex = themeName.startsWith('custom:')
          ? themeName.slice(7)
          : themes[themeName].primary;
        dispatchThemeChangedEvent(themeName, primaryHex);

        // Broadcast to other open tabs via storage event
        try {
          localStorage.setItem('ecoh-theme-sync', themeName);
        } catch { /* ignore */ }
      },

      /**
       * Toggle dark mode
       */
      toggleDarkMode: () => {
        const newDarkMode = !get().isDarkMode;

        logger.info('Toggling dark mode', { isDarkMode: newDarkMode });

        set({ isDarkMode: newDarkMode });

        // Apply DOM changes
        applyThemeToDOM(get().themeName, newDarkMode);

        // Persist to asyncStorage
        asyncStorage.set('darkMode', newDarkMode);
      },

      /**
       * Set dark mode explicitly
       * @param {boolean} darkMode - Dark mode enabled
       */
      setDarkMode: (darkMode) => {
        logger.info('Setting dark mode', { isDarkMode: darkMode });

        set({ isDarkMode: darkMode });

        // Apply DOM changes
        applyThemeToDOM(get().themeName, darkMode);

        // Persist to asyncStorage
        asyncStorage.set('darkMode', darkMode);
      },

      /**
       * Hydrate theme from user data (for deferred loading)
       * @param {Object} userData - User data object
       */
      hydrateFromUserData: (userData) => {
        // Never override a custom theme the user explicitly chose.
        // Check the Zustand state first; if persist rehydration hasn't
        // completed yet also check raw localStorage directly so we don't
        // race against the async rehydration cycle.
        const currentTheme = get().themeName;
        if (currentTheme?.startsWith('custom:')) return;
        try {
          const stored = asyncStorage.getJSON('theme-storage');
          if (stored?.state?.themeName?.startsWith('custom:')) return;
        } catch { /* ignore */ }

        const themeName = extractThemeFromUserData(userData);
        if (themeName && themeName !== currentTheme) {
          logger.debug('Hydrating theme from user data', { themeName });
          get().setTheme(themeName);
        }
      }
    }),
    createPersistConfig('theme-storage', {
      partialize: (state) => ({
        themeName: state.themeName,
        isDarkMode: state.isDarkMode
      })
    })
  )
);

// Initialize DOM on store creation
const initialState = useThemeStore.getState();
applyThemeToDOM(initialState.themeName, initialState.isDarkMode);

// Listen for theme changed events (e.g. admin changes org theme in settings)
if (typeof window !== 'undefined') {
  window.addEventListener('themeChanged', (event) => {
    const { theme: themeValue } = event.detail;
    // custom: and named themes pass through directly; bare hex codes are mapped
    const themeName = (!themeValue.startsWith('custom:') && themeValue.startsWith('#'))
      ? getThemeNameFromHex(themeValue)
      : themeValue;

    if (themeName !== useThemeStore.getState().themeName) {
      useThemeStore.getState().setTheme(themeName);
    }
  });

  // Sync theme across other open tabs — storage events only fire in OTHER tabs
  window.addEventListener('storage', (e) => {
    if (e.key !== 'ecoh-theme-sync' || !e.newValue) return;
    const themeName = e.newValue;
    if (themeName !== useThemeStore.getState().themeName) {
      useThemeStore.getState().setTheme(themeName);
    }
  });
}

// Granular selector hooks

/**
 * Get current theme name
 * @returns {string} Theme name
 */
export const useThemeName = () =>
  useThemeStore(state => state.themeName);

/**
 * Get dark mode status
 * @returns {boolean} Dark mode enabled
 */
export const useIsDarkMode = () =>
  useThemeStore(state => state.isDarkMode);

/**
 * Get current theme object
 * @returns {Object} Theme object with colors and settings
 */
export const useTheme = () =>
  useThemeStore(state => {
    const theme = state.themeName?.startsWith('custom:')
      ? generatePaletteFromHex(state.themeName.slice(7))
      : (themes[state.themeName] || themes.default);
    return {
      ...theme,
      colors: {
        light: lightModeColors,
        dark: darkModeColors
      }
    };
  });

/**
 * Get theme hex color
 * @returns {string} Primary hex color
 */
export const useThemeHex = () =>
  useThemeStore(state =>
    state.themeName?.startsWith('custom:')
      ? state.themeName.slice(7)
      : getHexFromThemeName(state.themeName)
  );

/**
 * Get all theme actions
 * @returns {Object} Theme actions
 */
export const useThemeActions = () =>
  useThemeStore(
    useShallow(state => ({
      setTheme: state.setTheme,
      toggleDarkMode: state.toggleDarkMode,
      setDarkMode: state.setDarkMode,
      hydrateFromUserData: state.hydrateFromUserData
    }))
  );

/**
 * Get all themes
 * @returns {Object} All theme definitions
 */
export const useAllThemes = () => themes;

/**
 * Get theme helper functions
 * @returns {Object} Helper functions
 */
export const useThemeHelpers = () => ({
  getThemeHex: getHexFromThemeName,
  getThemeFromHex: getThemeNameFromHex
});
